// pattern: Functional Core

/**
 * Progress Tracker - Mastery calculation for flashcard study
 * Functional Core - no side effects
 *
 * Mastery algorithm (from QuizWind):
 * - new: < 3 attempts
 * - learning: 3+ attempts, < 80% accuracy
 * - mastered: 5+ attempts, >= 80% accuracy
 */

export type MasteryLevel = 'new' | 'learning' | 'mastered';

export type FlashcardProgress = {
  readonly cardId: string;
  readonly attempts: number;
  readonly correctCount: number;
  readonly lastAttempted: number;
  readonly mastery: MasteryLevel;
};

/** Calculate mastery level from attempt counts */
export function calculateMastery(attempts: number, correctCount: number): MasteryLevel {
  if (attempts < 3) return 'new';
  const accuracy = correctCount / attempts;
  if (attempts >= 5 && accuracy >= 0.8) return 'mastered';
  return 'learning';
}

/** Create initial progress for an unstudied card */
export function createProgress(cardId: string): FlashcardProgress {
  return {
    cardId,
    attempts: 0,
    correctCount: 0,
    lastAttempted: 0,
    mastery: 'new',
  };
}

/** Return updated progress after a study attempt (immutable) */
export function updateProgress(
  progress: FlashcardProgress,
  correct: boolean,
  timestamp: number
): FlashcardProgress {
  const attempts = progress.attempts + 1;
  const correctCount = progress.correctCount + (correct ? 1 : 0);
  return {
    cardId: progress.cardId,
    attempts,
    correctCount,
    lastAttempted: timestamp,
    mastery: calculateMastery(attempts, correctCount),
  };
}
